/**
 * "See more" affordance for the TS product description block.
 *
 * The description is trimmed in JS so the "… see more" toggle flows inline
 * right after the truncated text (no gap to the box edge). On the PDP the
 * button scrolls to the ts-pdp-details section; inside the quick view /
 * shop-the-look dialogs (where that section doesn't exist) it falls back to
 * a "see more / see less" expand toggle. The toggle only shows when the
 * full text actually overflows the 3-line clamp.
 */
class TsProductDescription extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLElement | null} */
  #text = null;

  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLElement | null} */
  #toggle = null;

  #fullHTML = '';

  #lastWidth = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retruncateTimer = undefined;

  #inited = false;

  connectedCallback() {
    if (!this.#inited) {
      this.#inited = true;

      this.#text = this.querySelector('.ts-product-description__text');
      this.#content = this.querySelector('.ts-product-description__content');
      this.#toggle = this.querySelector('.ts-product-description__toggle');
      if (!this.#text || !this.#content || !this.#toggle) return;

      this.#fullHTML = this.#content.innerHTML;

      // On the PDP the button navigates instead of toggling.
      if (!this.closest('dialog')) this.#toggle.removeAttribute('aria-expanded');

      this.#toggle.addEventListener('click', () => this.#handleClick());
      // Re-truncate on width changes only (height changes are our own
      // trimming and would loop).
      this.#resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        if (Math.abs(width - this.#lastWidth) < 1) return;
        this.#lastWidth = width;
        // Debounce until the resize settles: re-truncation forces a layout
        // per binary-search step — too heavy to run on every resize frame.
        clearTimeout(this.#retruncateTimer);
        this.#retruncateTimer = setTimeout(() => this.#updateTruncation(), 150);
      });
      // Wrap points shift once custom fonts finish loading.
      document.fonts?.ready.then(() => this.#updateTruncation());
    }

    if (!this.#resizeObserver || !this.#text) return;
    this.#resizeObserver.observe(this.#text);
    this.#updateTruncation();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#retruncateTimer);
  }

  #handleClick() {
    if (!this.#text || !this.#toggle) return;

    const details = this.closest('dialog') ? null : document.querySelector('.ts-pdp-details__container');
    if (details instanceof HTMLElement) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Skip-link pattern: move focus so keyboard/SR users land on the details.
      details.setAttribute('tabindex', '-1');
      details.focus({ preventScroll: true });
      details.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth', block: 'start' });
      return;
    }

    const expanded = this.#text.dataset.state === 'expanded';
    this.#text.dataset.state = expanded ? 'clamped' : 'expanded';
    this.#toggle.setAttribute('aria-expanded', String(!expanded));
    this.#toggle.textContent = expanded ? 'see more' : 'see less';
    if (expanded) {
      this.#updateTruncation();
    } else {
      this.#restoreFullContent();
    }
    // Re-parenting the toggle resets document.activeElement to <body>.
    this.#toggle.focus();
  }

  // Restore the untrimmed description with the toggle parked after it.
  #restoreFullContent() {
    if (!this.#text || !this.#content || !this.#toggle) return;
    // Move the toggle out first — rewriting innerHTML would destroy it.
    this.#text.appendChild(this.#toggle);
    this.#content.innerHTML = this.#fullHTML;
  }

  // Show the toggle only when the full text overflows the clamp, then trim
  // the text so "… see more" fits inline at the end of the last line.
  #updateTruncation() {
    if (!this.#text || !this.#content || !this.#toggle) return;
    if (this.#text.dataset.state === 'expanded') return;

    this.#restoreFullContent();
    this.#text.removeAttribute('data-truncated');

    const truncated = this.#overflows();
    this.#text.toggleAttribute('data-truncated', truncated);
    if (!truncated) return;

    // Binary-search the largest character budget that still fits with the
    // inline toggle in place.
    let low = 0;
    let high = (this.#content.textContent ?? '').length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      this.#applyTruncation(mid);
      if (this.#overflows()) {
        high = mid - 1;
      } else {
        low = mid;
      }
    }
    this.#applyTruncation(low);
  }

  #overflows() {
    if (!this.#text) return false;
    return this.#text.scrollHeight > this.#text.clientHeight + 1;
  }

  /**
   * Rebuild the description keeping roughly `budget` characters (backed off
   * to a word boundary), append an ellipsis to the cut text, and move the
   * toggle inline after it so both render on the same line.
   * @param {number} budget
   */
  #applyTruncation(budget) {
    if (!this.#text || !this.#content || !this.#toggle) return;

    this.#restoreFullContent();

    const walker = document.createTreeWalker(this.#content, NodeFilter.SHOW_TEXT);
    let remaining = budget;
    /** @type {Text | null} */
    let cutNode = null;
    let node;
    while ((node = /** @type {Text | null} */ (walker.nextNode()))) {
      if (remaining >= node.data.length) {
        remaining -= node.data.length;
        if (node.data.trim()) cutNode = node;
        continue;
      }
      const clipped = node.data.slice(0, remaining).replace(/\s+\S*$/, '');
      node.data = clipped;
      if (clipped.trim()) cutNode = node;
      break;
    }
    if (!cutNode) return;

    // Drop everything after the cut so emptied blocks don't add height.
    /** @type {Node} */
    let current = cutNode;
    while (current.parentNode && current !== this.#content) {
      while (current.nextSibling) {
        /** @type {ChildNode} */ (current.nextSibling).remove();
      }
      current = current.parentNode;
    }

    cutNode.data = cutNode.data.replace(/[\s.,;:]+$/, '') + '…';

    /** @type {Node} */
    let block = cutNode;
    while (block.parentNode && block.parentNode !== this.#content) {
      block = block.parentNode;
    }
    if (block instanceof Element) {
      block.appendChild(this.#toggle);
    } else {
      cutNode.after(this.#toggle);
    }
  }
}

if (!customElements.get('ts-product-description')) {
  customElements.define('ts-product-description', TsProductDescription);
}
